/**
 * FEAT-113 — GA4 and PostHog for app.nagareru.ai, loaded only after the
 * visitor said "Allow" on nagareru.ai (see analytics-consent.ts). Same GA4
 * property and PostHog project as thecmolab.ai and the nagareru.ai landing
 * page, so a visitor can be followed from the landing page into the app.
 *
 * The app shows people's own writing, so PostHog masks every piece of text
 * and every input, in autocapture and in session replays. Nothing a user
 * writes leaves the browser (measurement-taxonomy.md §12).
 */

import { analyticsConfig, readConsent } from "./analytics-consent";

const CONTEXT = { site_section: "app", page_type: "app", content_language: "en" };

function loadScript(src: string) {
  const s = document.createElement("script");
  s.async = true;
  s.src = src;
  document.head.appendChild(s);
}

/** `?internal=1` marks this browser as internal traffic, as on the sites. */
function isInternal(): boolean {
  try {
    const p = new URLSearchParams(location.search).get("internal");
    if (p === "1") localStorage.setItem("cmolab_internal", "1");
    if (p === "0") localStorage.removeItem("cmolab_internal");
    return localStorage.getItem("cmolab_internal") === "1";
  } catch {
    return false;
  }
}

function loadGA(id: string, ctx: Record<string, string>) {
  window.dataLayer = window.dataLayer ?? [];
  window.gtag = function (...args: unknown[]) {
    window.dataLayer.push(args);
  };
  window.gtag("consent", "default", {
    analytics_storage: "granted",
    ad_storage: "denied",
    ad_user_data: "denied",
    ad_personalization: "denied",
  });
  window.gtag("js", new Date());
  // Page views are sent by <Analytics /> on each route change.
  window.gtag("config", id, {
    send_page_view: false,
    allow_google_signals: false,
    allow_ad_personalization_signals: false,
    ...ctx,
  });
  loadScript(`https://www.googletagmanager.com/gtag/js?id=${id}`);
}

/** PostHog's array.js snippet: queues calls until the library arrives. */
function loadPostHog(key: string, host: string, ctx: Record<string, string>) {
  const ph = (window.posthog = window.posthog ?? ([] as unknown as PostHogStub));
  if (ph.__SV) return;
  ph._i = [];
  ph.init = (k, opts) => {
    for (const m of ["capture", "identify", "register", "reset", "opt_out_capturing"] as const) {
      ph[m] = (...args: unknown[]) => {
        ph.push([m, ...args]);
      };
    }
    ph._i!.push([k, opts]);
  };
  ph.__SV = 1;
  loadScript(`${host.replace(".i.posthog.com", "-assets.i.posthog.com")}/static/array.js`);

  ph.init(key, {
    api_host: host,
    person_profiles: "identified_only",
    capture_pageview: false,
    capture_pageleave: true,
    autocapture: true,
    mask_all_text: true,
    mask_all_element_attributes: true,
    session_recording: { maskAllInputs: true, maskTextSelector: "*" },
    persistence: "localStorage+cookie",
  });
  ph.register(ctx);
}

export function startAnalytics() {
  const cfg = analyticsConfig(readConsent(document.cookie), {
    gaId: import.meta.env.VITE_GA_MEASUREMENT_ID,
    posthogKey: import.meta.env.VITE_POSTHOG_KEY,
    posthogHost: import.meta.env.VITE_POSTHOG_HOST,
  });
  if (!cfg.ga && !cfg.posthog) return;
  const ctx: Record<string, string> = { ...CONTEXT };
  if (isInternal()) ctx.traffic_type = "internal";
  if (cfg.ga) loadGA(cfg.ga, ctx);
  if (cfg.posthog) loadPostHog(cfg.posthog.key, cfg.posthog.host, ctx);
}
